import { useEffect, useState, type FormEvent } from 'react';
import { ROLE_RANK, type Role } from '@goss/types';
import { apiFetch } from '../lib/api.js';
import { C, alpha } from '../lib/theme.js';
import { Button, Card, ErrorLine } from '../ui/ui.js';
import type { Invite, Member, OrgSummary } from '../lib/types.js';

export function OrgPanel({ org }: { org: OrgSummary }) {
  const [role, setRole] = useState<Role | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function loadMembers() {
    try {
      const data = await apiFetch<{ role: Role; members: Member[] }>(`/api/orgs/${org.id}/members`);
      setRole(data.role);
      setMembers(data.members);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    }
  }

  useEffect(() => {
    setRole(null);
    setMembers([]);
    setError(null);
    void loadMembers();
  }, [org.id]);

  const canInvite = role !== null && ROLE_RANK[role] >= ROLE_RANK.admin;

  return (
    <div>
      {error && <ErrorLine>{error}</ErrorLine>}
      <Card style={{ overflow: 'hidden', marginBottom: 14 }}>
        {members.map((m) => (
          <div
            key={m.user_id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 18px',
              borderBottom: `1px solid ${C.lineSoft}`,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: m.accepted_at ? C.good : C.watch,
              }}
            />
            <span style={{ fontSize: 13, color: C.primary }}>{m.display_name ?? m.user_id}</span>
            <span
              style={{
                marginLeft: 'auto',
                font: `500 10px 'IBM Plex Mono',monospace`,
                color: C.muted,
                textTransform: 'uppercase',
                letterSpacing: '.08em',
              }}
            >
              {m.role}
              {m.accepted_at ? '' : ' · pending'}
            </span>
          </div>
        ))}
        {members.length === 0 && (
          <div style={{ padding: '12px 18px', color: C.faint, fontSize: 13 }}>No members yet.</div>
        )}
      </Card>

      {canInvite && <InviteForm orgId={org.id} onInvited={loadMembers} />}

      <RolesLegend />
    </div>
  );
}

const ROLE_INFO: { role: Role; color: string; summary: string; can: string; cant: string }[] = [
  {
    role: 'owner',
    color: C.basil,
    summary: 'Full control of the workspace.',
    can: 'Everything an admin can, plus manage the API keys / billing and delete or transfer the organization.',
    cant: 'Nothing is restricted.',
  },
  {
    role: 'admin',
    color: C.info,
    summary: 'Runs the workspace.',
    can: 'Manage the team, edit all settings, connect Meta / Cloudbeds / Anthropic, build campaigns AND take them live (start spend).',
    cant: 'Delete the organization or change the owner.',
  },
  {
    role: 'marketer',
    color: C.good,
    summary: 'Creates and prepares the work.',
    can: 'Write copy, compose creative, manage media, save posts, build campaigns (paused) and sync data.',
    cant: 'Change settings, manage the team or connections, or take a campaign LIVE (that is admin-only).',
  },
  {
    role: 'viewer',
    color: C.muted,
    summary: 'Read-only.',
    can: 'View dashboards, campaigns, posts, media and bookings.',
    cant: 'Create, edit, launch or change anything.',
  },
];

function RolesLegend() {
  const [sel, setSel] = useState<Role>('admin');
  const r = ROLE_INFO.find((x) => x.role === sel) ?? ROLE_INFO[1]!;
  return (
    <div style={{ marginTop: 18 }}>
      <div className="label-mono" style={{ marginBottom: 8 }}>
        Roles &amp; permissions
      </div>
      {/* segmented role tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {ROLE_INFO.map((x) => {
          const on = x.role === sel;
          return (
            <button
              key={x.role}
              type="button"
              onClick={() => setSel(x.role)}
              style={{
                padding: '6px 13px',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: 'inherit',
                font: `600 11px 'IBM Plex Mono',monospace`,
                textTransform: 'uppercase',
                letterSpacing: '.06em',
                background: on ? alpha(x.color, 16) : 'transparent',
                border: `1px solid ${on ? alpha(x.color, 55) : C.line}`,
                color: on ? x.color : C.muted,
              }}
            >
              {x.role}
            </button>
          );
        })}
      </div>
      <Card style={{ padding: '13px 16px' }}>
        <div style={{ fontSize: 13, color: C.primary, fontWeight: 600, marginBottom: 6 }}>
          {r.summary}
        </div>
        <div style={{ fontSize: 12.5, color: C.secondary, lineHeight: 1.55 }}>
          <span style={{ color: C.good, fontWeight: 600 }}>Can:</span> {r.can}
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginTop: 3 }}>
          <span style={{ color: C.act, fontWeight: 600 }}>Can’t:</span> {r.cant}
        </div>
      </Card>
    </div>
  );
}

function InviteForm({ orgId, onInvited }: { orgId: string; onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('marketer');
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setLink(null);
    try {
      const invite = await apiFetch<Invite>(`/api/orgs/${orgId}/invites`, {
        method: 'POST',
        body: JSON.stringify({ email, role: inviteRole }),
      });
      setLink(`${window.location.origin}/accept-invite?token=${invite.token}`);
      setEmail('');
      onInvited();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <form
        onSubmit={submit}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <input
          className="field"
          type="email"
          required
          placeholder="teammate@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ minWidth: 240, flex: 1, maxWidth: 320 }}
        />
        <select
          className="field"
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as Role)}
          style={{ width: 'auto', cursor: 'pointer' }}
        >
          <option value="admin">admin</option>
          <option value="marketer">marketer</option>
          <option value="viewer">viewer</option>
        </select>
        <Button variant="primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create invite link'}
        </Button>
      </form>
      <div style={{ marginTop: 7, fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
        Generates a shareable link for the chosen role. No email is sent — copy the link and send it
        yourself. The person must also be on the access allowlist to sign up.
      </div>
      {error && <ErrorLine>{error}</ErrorLine>}
      {link && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: C.muted }}>
          Invite link (email sending comes later — share manually for now):
          <div
            style={{
              marginTop: 4,
              font: `500 11px 'IBM Plex Mono',monospace`,
              color: C.info,
              wordBreak: 'break-all',
            }}
          >
            {link}
          </div>
        </div>
      )}
    </div>
  );
}
